import { useContext } from 'react'

import { useCallUpdateWidget, useWidgetDeleteApi } from '@/common/api/modules/widget'
import { WIDGET_STATUS_CHOICES, widgetDisplayLogicName } from '@/common/app/modules/recommend/widget'
import { widgetEditPath, widgetNewPath } from '@/common/app/path'
import { Widget, WidgetContentType, WidgetStatus } from '@/common/model/recommend/widget'

import Badge, { BadgeType } from '@/components/view/atoms/badge/Badge'
import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import TextButton from '@/components/view/atoms/button/TextButton'
import FormLabel from '@/components/view/atoms/form/FormLabel'
import Heading from '@/components/view/atoms/heading/Heading'
import Icon, { IconColor, IconType } from '@/components/view/atoms/icon/Icon'
import Text from '@/components/view/atoms/text/Text'
import { TextBoxSize } from '@/components/view/atoms/textfield/TextField'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'
import FormAlign from '@/components/view/layouts/form/FormAlign'
import FormRow from '@/components/view/layouts/form/FormRow'
import DisclosureSelector from '@/components/view/molecules/selector/DisclosureSelector'

import { WidgetCssModalContext } from '@/components/container/modal/widget/css/WidgetCssModalProvider'
import { WidgetDesignModalContext } from '@/components/container/modal/widget/design/WidgetDesignModalProvider'

interface Props {
  widget: Widget
  onRefresh: () => void
}

const WidgetBlock = ({ widget, onRefresh }: Props) => {
  const { trigger: update } = useCallUpdateWidget({
    onSuccess: () => onRefresh()
  })
  const { onClickDelete } = useWidgetDeleteApi(widget, onRefresh)
  const { showModal: showCssModal } = useContext(WidgetCssModalContext)
  const { showModal: showDesignModal } = useContext(WidgetDesignModalContext)

  return (
    <Area noBorder>
      <Heading
        rightNode={
          <>
            <Button link={widgetEditPath(widget.site_id, widget.id)} color={ButtonColor.SECONDARY}>
              編集する
            </Button>
            <Button
              link={`${widgetNewPath(widget.site_id)}?copiedId=${widget.id}`}
              color={ButtonColor.SECONDARY}
              square
            >
              <Icon type={IconType.COPY_PLUS} />
            </Button>
            <Button color={ButtonColor.SECONDARY} square onClick={onClickDelete}>
              <Icon type={IconType.DELETE} color={IconColor.CRITICAL} line />
            </Button>
          </>
        }
      >
        <FormRow gap={16} align='center'>
          <FormRow gap={8} size={TextBoxSize.FIT} align='center'>
            <Badge badgeType={BadgeType.SQUARE_PRIMARY} size={10}>
              {widget.content_type == WidgetContentType.PRODUCT ? '商品' : '記事'}
            </Badge>
            {widget.name}
          </FormRow>
          <DisclosureSelector<string>
            value={widget.status}
            name={'status'}
            choices={WIDGET_STATUS_CHOICES}
            onChange={(value) =>
              update({
                site_id: widget.site_id,
                widget_id: widget.id,
                status: value as WidgetStatus
              })
            }
            remnant
          />
          <Button color={ButtonColor.SECONDARY} square onClick={() => showDesignModal({ widget })}>
            <Icon type={IconType.PREVIEW} />
          </Button>
        </FormRow>
      </Heading>
      <AreaContent>
        <div className='flex gap-16'>
          <FormAlign direction={'vertical'} align={'start'}>
            <FormLabel>レコメンドロジック</FormLabel>
            <Text>{widgetDisplayLogicName(widget.display_logic)}</Text>
          </FormAlign>
          <FormAlign direction={'vertical'} align={'start'}>
            <FormLabel>カスタムCSS</FormLabel>
            <FormAlign>
              {widget.custom_css ? (
                <>
                  <Text>設定あり</Text>
                  <TextButton arrow onClick={() => showCssModal({ customCss: widget.custom_css })}>
                    詳細を見る
                  </TextButton>
                </>
              ) : (
                <Text>設定なし</Text>
              )}
            </FormAlign>
          </FormAlign>
        </div>
      </AreaContent>
    </Area>
  )
}

export default WidgetBlock
